import React, { useRef, useState } from "react";
import { getVideoUploadUrl, deleteVideo, deleteImage } from "../../api/messageRequests";
import { useTranslation } from "../../i18n/TranslationProvider";

import * as tus from "tus-js-client";

// the maximum length for a video is set to 1 minute, 30 seconds

const MAX_DURATION_SECONDS = 90;

// Reads the video file locally (before uploading) to check its duration.
const getVideoDuration = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url); // clean up the temporary URL
      resolve(video.duration);  // note: duration is in seconds
    };
    video.src = url;
  });

// For local development, skip the real upload and simulate progress instead. this is for before we have the API Endpoint.
// const mockUpload = async (setProgress) => {
//   for (let i = 0; i <= 100; i += 20) {
//     setProgress(i);
//     await new Promise((r) => setTimeout(r, 200));
//   }
//   return `mock-uid-${Date.now()}`;
// };

// Asks our backend for a one-time upload URL.
const getUploadUrl = async (file) => {
  const res = await getVideoUploadUrl(file);
  return res.data; // axios wraps the response in .data
};


// Tus upload, now using tus-js-client instead of manually configuring the chuncks/offsets. This should be more reliable
const tusUpload = (file, uploadUrl, setProgress) => {
  return new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      uploadUrl,

      // Retry transient failures
      retryDelays: [0, 1000, 3000, 5000],
      chunkSize: 50 * 1024 * 1024,
      storeFingerprintForResuming: false,
      removeFingerprintOnSuccess: true,
      // Cloudflare metadata
      metadata: {
        filename: file.name,
        filetype: file.type,
      },

      onError(error) {
        console.error("TUS Upload Error:", error);
        reject(error);
      },

      onProgress(bytesUploaded, bytesTotal) {
        const percentage = Math.round(
          (bytesUploaded / bytesTotal) * 100
        );

        setProgress(percentage);

        console.log(
          `Upload progress: ${bytesUploaded}/${bytesTotal} (${percentage}%)`
        );
      },

      onSuccess() {
        console.log("Upload completed successfully");
        resolve(upload.url);
      },
    });

    // If the upload was interrupted previously,
    // resume it instead of starting over. - This was causing issues, REMOVED
    // upload.findPreviousUploads().then((previousUploads) => {
    //   if (previousUploads.length) {
    //     console.log("Resuming previous upload");
    //     upload.resumeFromPreviousUpload(previousUploads[0]);
    //   }

    //   upload.start();
    // }).catch((err) => {
    //   console.error("Failed checking previous uploads:", err);
    //   upload.start();
    // });

    upload.start();
  });
};

const VideoUploader = ({ onUploadComplete, onStatusChange, onManualCancel }) => {
   const { t } = useTranslation();
  // "idle" | "uploading" | "success" | "error"
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState(null);
  const [uploadedUid, setUploadedUid] = useState(null);

  const inputRef = useRef(null);

  // update the parent of the current status
  const updateStatus = (newStatus) => {
    setStatus(newStatus);
    if (onStatusChange) onStatusChange(newStatus);
  };
  

  const handleFile = async (file) => {
    if (!file) return;

    // Check duration before doing anything else
    const duration = await getVideoDuration(file);
    if (duration > MAX_DURATION_SECONDS) {
      alert(`${t("messages.videoUploader.over1MinWarning")} ${Math.round(duration)}s).`);
      return;
    }

    updateStatus("uploading");
    setFileName(file.name);

    try {
      let uid;

      const { uploadUrl, uid: cloudflareUid } = await getUploadUrl(file);
      await tusUpload(file, uploadUrl, setProgress);
      setUploadedUid(cloudflareUid);
      updateStatus("success");
      onUploadComplete(cloudflareUid); // pass the uid up to SendMessage
      
    } catch (err) {
      console.error("Video upload failed:", err);
      updateStatus("error");
    }
  };

  const handleRemove = async () => {
     if (onManualCancel) onManualCancel();
     console.log("deleting uid:", uploadedUid);
    if (uploadedUid) {
        try {
          // API call
          await deleteVideo(uploadedUid); 
        } catch (err) {
          console.error("Failed to delete video from Cloudflare:", err);
        }
      }
    setUploadedUid(null);
    updateStatus("idle");
    setProgress(0);
    setFileName(null);
    if (inputRef.current) inputRef.current.value = "";
    onUploadComplete(null); // clear the uid in SendMessage
  };

  return (
    <div className="my-2">

      {/* Click zone — clicking this triggers the hidden file input below */}
      {status === "idle" && (
        <div
          onClick={() => inputRef.current?.click()}
          style={{ border: "2px dashed #adb5bd", borderRadius: 8, padding: 16, textAlign: "center", cursor: "pointer", background: "#f8f9fa", fontSize: 13, color: "#6c757d" }}
        >
          {t("messages.videoUploader.selectVideo")}
          {/* Hidden input — "accept" limits the file picker to videos only */}
          <input
            ref={inputRef}
            type="file"
            accept="video/*"
            style={{ display: "none" }}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
      )}

      {/* Progress bar */}
      {status === "uploading" && (
        <div>
          <div style={{ fontSize: 12, color: "#6c757d", marginBottom: 4 }}>
            {t("messages.videoUploader.uploading")}… {progress}%
          </div>
          <div style={{ background: "#e9ecef", borderRadius: 4, height: 6 }}>
            <div style={{ width: `${progress}%`, height: "100%", background: "#0d6efd", borderRadius: 4, transition: "width 0.3s" }} />
          </div>
        </div>
      )}

      {/* Success — shows filename with an X to remove */}
      {status === "success" && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "#d1e7dd", borderRadius: 6, fontSize: 13 }}>
          <span>{fileName}</span>
          <button type="button" onClick={handleRemove} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#842029", fontWeight: "bold", fontSize: 16 }}>×</button>
        </div>
      )}

      {/* Error */}
      {status === "error" && (
        <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 10px", background: "#f8d7da", borderRadius: 6, fontSize: 13, color: "#842029" }}>
          <span>
            {t("messages.videoUploader.uploadFailed")}
            <p className="small">{t("messages.videoUploader.failureMsg")}</p>
          </span>
          <button type="button" onClick={handleRemove} style={{ background: "none", border: "none", cursor: "pointer", color: "#842029", fontWeight: "bold" }}>×</button>
        </div>
      )}

    </div>
  );
};

export default VideoUploader;